import Splide, { Options } from "@splidejs/splide";

/* Interfaces */
interface IAriaTranslations {
    prev: string;
    next: string;
    first: string;
    last: string;
    slideX: string;
    carousel: string;
    select: string;
    slide: string;
    slideLabel: string;
}

export default class EntrySection {
    readonly entrySections: NodeListOf<HTMLElement>;
    readonly entrySectionCarouselOptions: Options;
    private ariaTranslations: IAriaTranslations | undefined = undefined;

    constructor() {
        this.entrySections = document.querySelectorAll('.entry-section-block--wrapper');
        
        this.entrySectionCarouselOptions = {
            type: 'loop',
            speed: 500,
            width: '100%',
            fixedWidth: '400px',
            clones: 0,
            start: 0,
            focus: 0,
            perMove: 1,
            easing: 'ease',
            drag: true,
            flickPower: 150,
            flickMaxPages: 1,
            arrowPath: 'M9.76407 0.764069C10.7828 -0.25469 12.4346 -0.25469 13.4533 0.764069L30.8446 18.1554C31.3339 18.6446 31.6087 19.3081 31.6087 20C31.6087 20.6919 31.3339 21.3554 30.8446 21.8446L13.4533 39.2359C12.4346 40.2547 10.7828 40.2547 9.76407 39.2359C8.74531 38.2172 8.74531 36.5654 9.76407 35.5467L25.3107 20L9.76407 4.45332C8.74531 3.43456 8.74531 1.78283 9.76407 0.764069Z',
            keyboard: true,
            updateOnMove: true,
            pagination: false,
            arrows: false,
            breakpoints: {
                0: {
                    fixedWidth: '90%',
                },
                768: {
                    fixedWidth: '400px',
                },
            },
            mediaQuery: 'min',
            classes: {
                arrow: 'entry-section-block__arrow splide__arrow',
                prev: 'entry-section-block__arrow--prev splide__arrow--prev ',
                next: 'entry-section-block__arrow--next splide__arrow--next '
            }
        }

        this.init();
    }

    private init(): void {
        if (this.entrySections.length > 0) {
            this.entrySections.forEach(entrySection => {
                if (entrySection.classList.contains("has-partials")) {
                    this.setUpCarousel(entrySection);
                }
            });
        }
    }

    private setUpCarousel(entrySection: HTMLElement): void {
        const carouselSection = entrySection.querySelector(".entry-section-block__carousel") as HTMLElement;

        const options: Options = {
            ...this.entrySectionCarouselOptions,
            breakpoints: {
                ...this.entrySectionCarouselOptions.breakpoints,
            },
        };

        if (!this.ariaTranslations) {
            this.setAriaTranslations(carouselSection);
            options.i18n = this.ariaTranslations;
        }

        const slidesContainerElement = entrySection.querySelector(".entry-section-block__slides-container") as HTMLElement;
        if (slidesContainerElement && slidesContainerElement.classList.contains("entry-section-block__slides-container--large")) {
            // Change size settings to large
            const LARGE_WIDTH = '919px';

            options.fixedWidth = LARGE_WIDTH;

            const largeBreakpoints = {
                0: { fixedWidth: '90%' },
                768: { fixedWidth: '90%' },
                992: { fixedWidth: LARGE_WIDTH }
            };

            options.breakpoints = largeBreakpoints;
        }

        const carouselId: string = carouselSection.id;
        const carousel: Splide = new Splide(`#${carouselId}`, options);

        carousel.mount();

        // Go to the previous slide
        entrySection.querySelectorAll('button.entry-section-block__arrow--prev')?.forEach((arrowElement) => {
            arrowElement.addEventListener('click', () => {
                carousel.go('<');
            });
        });

        // Go to the next slide
        entrySection.querySelectorAll('.entry-section-block__arrow--next')?.forEach((arrowElement) => {
            arrowElement.addEventListener('click', () => {
                carousel.go('>');
            });
        });
    }

    private setAriaTranslations(carousel: HTMLElement): void {
        let ariaTranslationsString = carousel.dataset.ariaText as string;
        let ariaTranslationsArray = ariaTranslationsString.split(', ') as string[];

        let ariaTranslationsObject: IAriaTranslations = {
            prev: ariaTranslationsArray[0],
            next: ariaTranslationsArray[1],
            first: ariaTranslationsArray[2],
            last: ariaTranslationsArray[3],
            slideX: ariaTranslationsArray[4],
            carousel: ariaTranslationsArray[5],
            select: ariaTranslationsArray[6],
            slide: ariaTranslationsArray[7],
            slideLabel: ariaTranslationsArray[8]
        }

        this.ariaTranslations = ariaTranslationsObject;
    }
}

function initParallax() {
    const sections = document.querySelectorAll<HTMLElement>(
        '.entry-section-block'
    );

    let ticking = false;

    function update() {
        const vh = window.innerHeight;

        sections.forEach(section => {
            const imageWrapper = section.querySelector<HTMLElement>(
                '.entry-section-block__parallax-img'
            );

            if (!imageWrapper) return;

            const rect = section.getBoundingClientRect();
            const imageHeight = imageWrapper.offsetHeight;

            const imageTop = rect.bottom - imageHeight;
            const shouldLock = imageTop <= 0;

            if (shouldLock) {
                imageWrapper.classList.add('is-fixed');

                const visibleTop = Math.max(0, rect.top);
                const visibleBottom = Math.min(vh, rect.bottom);

                let clipTop = visibleTop;
                let clipBottom = vh - visibleBottom;

                clipTop = Math.max(0, Math.min(vh, clipTop));
                clipBottom = Math.max(0, Math.min(vh, clipBottom));

                imageWrapper.style.clipPath =
                    `inset(${clipTop}px 0px ${clipBottom}px 0px)`;

            } else {
                imageWrapper.classList.remove('is-fixed');
                imageWrapper.style.clipPath = '';
            }
        });

        ticking = false;
    }

    function onScroll() {
        if (!ticking) {
            requestAnimationFrame(update);
            ticking = true;
        }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    update();
}

function initClickableEntryBlocks() {
    document.querySelectorAll('.entry-block').forEach(entryBlock => {
        entryBlock.addEventListener('click', () => {
            let link = entryBlock.querySelector('.entry-block__link') as HTMLAnchorElement;
            
            if (!link) {
                return;
            }
            
            let linkUrl = link.getAttribute('href') as string;
            
            window.location.href = linkUrl;
        });
    });
}


if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new EntrySection();
        initParallax();
        initClickableEntryBlocks();
    });
} else {
    new EntrySection();
    initParallax();
    initClickableEntryBlocks();
}